import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { TitleBarComponent } from '../../shared/components/title-bar/title-bar.component';
import {
  ActivityEvent,
  DrbSyncShop,
  PlateAlert,
  PlateLookupCustomer,
  PlateLookupMembership,
  PlateLookupResult,
  PlateLookupService,
  PlateLookupShopSummary,
  PurchaseRow,
} from '../services/plate-lookup.service';
import {
  ACTIVITY_FILTERS,
  ACTIVITY_LABEL,
  DASH,
  DRB_REASON_HINT,
  activityDetail,
  alertCopy,
  formatDate,
  formatDateTime,
  formatMonth,
  importActivityTitle,
  isImportActivity,
  membershipStatusLabel,
  membershipTone,
  money,
  planCoversPlate,
  planPlate,
  purchaseKindLabel,
  sourceLabel,
} from './plate-lookup-copy';
import { isCustomerInactive } from '../../shared/utils/customer-status.util';

/** Matches the backend: letters and digits only, 2 to 12 of them. */
const PLATE_RE = /^[A-Z0-9]{2,12}$/;
const STATE_RE = /^[A-Z]{2}$/;

export type PlateLookupTab = 'activity' | 'import' | 'membership' | 'purchases';

interface ActivityGroup {
  month: string;
  events: ActivityEvent[];
}

/**
 * Super-admin Plate Lookup (CWS-1703): what happened to one plate, across every shop.
 *
 * Activity comes first (LPR scans, redemptions, rewashes, purchases, renewals,
 * membership changes, newest first); Import Status, Membership Status and Purchases
 * follow as tabs; customer and subscriptions sit in one card beside it. The plate
 * lives in the URL (`?plate=&state=`) so a lookup can be shared or reloaded.
 */
@Component({
  selector: 'app-plate-lookup',
  standalone: true,
  imports: [CommonModule, FormsModule, TitleBarComponent],
  templateUrl: './plate-lookup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./plate-lookup.component.scss'],
})
export class PlateLookupComponent implements OnInit {
  private readonly plateLookupService = inject(PlateLookupService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly activityFilters = ACTIVITY_FILTERS;
  readonly activityLabel = ACTIVITY_LABEL;
  readonly dash = DASH;
  readonly activityDetail = activityDetail;
  readonly formatDate = formatDate;
  readonly formatDateTime = formatDateTime;
  readonly importActivityTitle = importActivityTitle;
  readonly isImportActivity = isImportActivity;
  readonly membershipStatusLabel = membershipStatusLabel;
  readonly membershipTone = membershipTone;
  readonly money = money;
  readonly planPlate = planPlate;
  readonly purchaseKindLabel = purchaseKindLabel;
  readonly sourceLabel = sourceLabel;

  plateInput = '';
  stateInput = '';
  validationMessage = '';

  loading = false;
  errorMessage = '';
  result: PlateLookupResult | null = null;

  /** null = every shop. */
  selectedShopId: string | null = null;
  activeTab: PlateLookupTab = 'activity';
  activityFilter: string | null = null;
  showOtherVehicles = false;
  drbLocationId = '';

  activityGroups: ActivityGroup[] = [];
  private requestSub?: Subscription;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const plate = params.get('plate');
      if (!plate) {
        this.clearLookup();
        return;
      }
      this.plateInput = plate;
      this.stateInput = params.get('state') ?? '';
      this.runLookup();
    });
  }

  /** Submit: put the plate in the URL; the query-param subscription runs the lookup. */
  search(): void {
    const plate = this.plateInput.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const state = this.stateInput.trim().toUpperCase();
    if (!PLATE_RE.test(plate)) {
      this.validationMessage = 'Enter 2 to 12 letters or digits.';
      return;
    }
    if (state && !STATE_RE.test(state)) {
      this.validationMessage = 'State must be a 2-letter code, like CA.';
      return;
    }
    this.validationMessage = '';

    const current = this.route.snapshot.queryParamMap;
    if (current.get('plate') === plate && (current.get('state') ?? '') === state) {
      // Same URL: navigation would not fire, so re-run directly (a deliberate refresh).
      this.plateInput = plate;
      this.stateInput = state;
      this.runLookup();
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { plate, state: state || null },
    });
  }

  // ── Shop scope ────────────────────────────────────────────────────────────

  get shops(): PlateLookupShopSummary[] {
    return this.result?.summary.shops ?? [];
  }

  /** The shop the status strip describes: the chosen one, else the first. */
  get stripShop(): PlateLookupShopSummary | null {
    return this.shops.find(s => s.companyId === this.selectedShopId) ?? this.shops[0] ?? null;
  }

  selectShop(companyId: string | null): void {
    this.selectedShopId = companyId;
    this.rebuildActivity();
  }

  private inScope<T extends { company_id?: string | null; companyId?: string | null }>(
    rows: T[]
  ): T[] {
    if (this.selectedShopId === null) return rows;
    return rows.filter(row => (row.company_id ?? row.companyId ?? null) === this.selectedShopId);
  }

  // ── Alerts and strip ──────────────────────────────────────────────────────

  get alerts(): { title: string; text: string; tone: 'warn' | 'info' }[] {
    const alerts: PlateAlert[] = this.result?.alerts ?? [];
    return alerts
      .filter(a => this.selectedShopId === null || a.companyId === this.selectedShopId)
      .map(alertCopy);
  }

  get stripStatus(): { label: string; tone: 'good' | 'warn' | 'grey' } {
    const shop = this.stripShop;
    if (shop?.activeMembership) return { label: 'Active', tone: 'good' };
    if (shop?.latestEnded) return { label: membershipStatusLabel(shop.latestEnded), tone: 'warn' };
    return { label: 'No subscription', tone: 'grey' };
  }

  // ── Activity ──────────────────────────────────────────────────────────────

  setActivityFilter(key: string | null): void {
    this.activityFilter = key;
    this.rebuildActivity();
  }

  toggleOtherVehicles(): void {
    this.showOtherVehicles = !this.showOtherVehicles;
    this.rebuildActivity();
  }

  get otherVehicleCount(): number {
    return this.inScope(this.result?.timeline.rows ?? []).filter(e => e.otherVehicle).length;
  }

  activityCount(key: string | null): number {
    return this.filteredActivity(key).length;
  }

  private filteredActivity(key: string | null): ActivityEvent[] {
    const filter = ACTIVITY_FILTERS.find(f => f.key === key);
    return this.inScope(this.result?.timeline.rows ?? []).filter(
      e => (this.showOtherVehicles || !e.otherVehicle) && (!filter || filter.types.includes(e.type))
    );
  }

  /** Group the filtered list by month in the shop's time zone, newest first. */
  private rebuildActivity(): void {
    const groups: ActivityGroup[] = [];
    for (const event of this.filteredActivity(this.activityFilter)) {
      const month = formatMonth(event.at, event.timezone);
      const last = groups[groups.length - 1];
      if (last && last.month === month) last.events.push(event);
      else groups.push({ month, events: [event] });
    }
    this.activityGroups = groups;
  }

  trackEvent(_: number, event: ActivityEvent): string {
    return event.id;
  }

  // ── Tabs ──────────────────────────────────────────────────────────────────

  get memberships(): PlateLookupMembership[] {
    return this.inScope(this.result?.sections.memberships.rows ?? []);
  }

  get purchases(): PurchaseRow[] {
    return this.inScope(this.result?.purchases.rows ?? []);
  }

  get importShops(): PlateLookupShopSummary[] {
    return this.selectedShopId === null ? this.shops : this.stripShop ? [this.stripShop] : [];
  }

  // ── Customer card ─────────────────────────────────────────────────────────

  /** Live customers first: a record switched off by a merge sits below the one it went into. */
  get customers(): PlateLookupCustomer[] {
    return [...this.inScope(this.result?.sections.customers.rows ?? [])].sort(
      (a, b) => Number(this.isInactiveCustomer(a)) - Number(this.isInactiveCustomer(b))
    );
  }

  /**
   * A switched-off record: an admin set the customer inactive, or a duplicate was merged into
   * another customer (the merge keeps the old row with `status = false` instead of deleting it).
   */
  isInactiveCustomer(c: PlateLookupCustomer): boolean {
    return c.status === false || isCustomerInactive(c.customer_status);
  }

  customerName(c: PlateLookupCustomer): string {
    return [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unnamed customer';
  }

  /** A customer's subscriptions, active first. */
  membershipsOf(customerId: string): PlateLookupMembership[] {
    return this.memberships
      .filter(m => m.customer_id === customerId)
      .sort((a, b) => Number(b.is_active) - Number(a.is_active));
  }

  /** The customer's plans for the searched car; one person can hold a plan per car. */
  plansForSearchedCar(customerId: string): PlateLookupMembership[] {
    const plate = this.result?.query.plate ?? '';
    return this.membershipsOf(customerId).filter(m => planCoversPlate(m, plate));
  }

  /** The customer's plans for their other cars, shown apart so they are not read as this car's. */
  plansForOtherCars(customerId: string): PlateLookupMembership[] {
    const plate = this.result?.query.plate ?? '';
    return this.membershipsOf(customerId).filter(m => !planCoversPlate(m, plate));
  }

  carsOf(customerId: string): { plate: string; description: string }[] {
    return (this.result?.sections.cars.rows ?? [])
      .filter(v => v.customer_id === customerId)
      .map(v => ({
        plate: v.license_plate,
        description: [v.year, v.make, v.model].filter(Boolean).join(' '),
      }));
  }

  washesOf(subscriptionId: string): { washes: number; lastWashAt: string | null } {
    return this.result?.washStats[subscriptionId] ?? { washes: 0, lastWashAt: null };
  }

  // ── Check with DRB ────────────────────────────────────────────────────────

  get drbShops(): DrbSyncShop[] {
    const shops = this.result?.drbSync.shops ?? [];
    return this.selectedShopId === null
      ? shops
      : shops.filter(shop => shop.companyId === this.selectedShopId);
  }

  get drbHint(): string {
    const shop =
      this.drbShops.find(s => s.locations.some(l => l.id === this.drbLocationId)) ??
      this.drbShops[0];
    return shop ? DRB_REASON_HINT[shop.reason] : '';
  }

  // ── Loading ───────────────────────────────────────────────────────────────

  /** Back to the empty page: a URL without a plate must not keep the last result. */
  private clearLookup(): void {
    this.requestSub?.unsubscribe();
    this.loading = false;
    this.errorMessage = '';
    this.result = null;
    this.selectedShopId = null;
    this.activityFilter = null;
    this.activityGroups = [];
    this.plateInput = '';
    this.stateInput = '';
  }

  private runLookup(): void {
    const plate = this.plateInput.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const state = this.stateInput.trim().toUpperCase();
    this.requestSub?.unsubscribe();
    this.loading = true;
    this.errorMessage = '';
    this.result = null;
    this.selectedShopId = null;
    this.activityFilter = null;
    this.showOtherVehicles = false;

    this.requestSub = this.plateLookupService.lookup(plate, state || null).subscribe({
      next: result => {
        this.result = result;
        const firstLocation = result.drbSync.shops[0]?.locations[0];
        this.drbLocationId = firstLocation?.id ?? '';
        this.rebuildActivity();
        this.loading = false;
      },
      error: err => {
        this.errorMessage = err?.error?.message || err?.message || 'Could not look up this plate.';
        this.loading = false;
      },
    });
  }
}
